// Fleet: what runs in the fleet over the app's range, against the span
// before. The Overview's tiles and running total on top (pages/OverviewPage.jsx);
// below, one inventory of agents, models,
// providers, endpoints and local inference (lib/fleetOverview.js),
// a tab for each, searched as Findings is: plain words, every one must match,
// matches marked, each tab counting its matches. Every number column's header
// sorts it, again the other way. A row opens in place: what it touches starts
// the inventory from that instead, and its findings open in Findings,
// searched for it. Links arrive at one agent by name (`fleetSearch`, the
// Overview's rows) or one endpoint (`fleetSelected`).
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DateRangePicker, { lengthOf } from "./DateRangePicker.jsx";
import { AgentIcon } from "./Dimension.jsx";
import { BUTTON, makerOf, useBlock, useSetRange } from "./FleetList.jsx";
import HoverTooltip from "./HoverTooltip.jsx";
import { useElementSize } from "./kernelPrimitives/shared.jsx";
import Highlight from "./ui/Highlight.jsx";
import SearchInput from "./ui/SearchInput.jsx";
import { CopyButton } from "./ui/Copy.jsx";
import { SkeletonRows } from "./ui/Skeleton.jsx";
import Tabs from "./ui/Tabs.jsx";
import { Pair, RunningTotal, Tile, useDoors } from "../pages/OverviewPage.jsx";
import { usePagedInventoryRows, useQuery } from "../lib/dataClient.js";
import { DEFAULT_RANGE } from "../lib/dateRangePresets.js";
import { formatAgo } from "../lib/escalationLabels.js";
import { searchWords } from "../lib/findingsConsole.js";
import { FLEET_STALE_MS } from "../lib/fleetConsole.js";
import { fleetWindows, setFleet, useFleet } from "../lib/fleetConsoleStore.js";
import { coverageOf, endpointsBefore } from "../lib/fleetCoverage.js";
import { COLUMNS, DEFAULT_SORT, TABS, TAB_IDS, agentRows, endpointRows, localRows, matchedVia, matches, modelRows, nextSort, providerRows, sortRows, sortable } from "../lib/fleetOverview.js";
import { agentCounts, agentsOf, fmt, moveText, numberer, pctText, runningTotal, tracesIn } from "../lib/overview.js";
import { hasPermission, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { spanWords } from "../lib/shell.js";
import { useDebouncedValue } from "../lib/useDebouncedValue.js";

const MUTED = "text-[color:var(--muted)]";
const INK = "text-[color:var(--ink)]";
const FOCUS = "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[color:var(--green)]";
// The rows: chevron, mark, name, bar, then the tab's columns, padded inside
// (PAD) so an open row's ground has room at both ends. A narrower page drops
// the columns after Change; a phone, the bar too. Every column keeps its
// width whatever the rows hold.
const PAD = "px-[12px]";
const GRID = {
  4: "grid-cols-[16px_24px_minmax(0,220px)_minmax(0,1fr)_76px_88px_76px_88px]",
  3: "grid-cols-[16px_24px_minmax(0,220px)_minmax(0,1fr)_76px_88px_76px]",
};
const NARROW = "@max-[980px]:grid-cols-[16px_24px_minmax(0,1fr)_minmax(0,160px)_76px_88px] @max-[740px]:grid-cols-[16px_24px_minmax(0,1fr)_76px_88px]";
const EXTRA = "@max-[980px]:hidden";
const BAR = "@max-[740px]:hidden";
// Rows shown before "Show all"; the endpoints are read a page at a time.
const FIRST = 40;
const PAGE = 50;
const EMPTY = {
  agents: "Agents appear here once Origin records an AI trace. Pick a longer range to look further back.",
  models: "Models appear here once an agent's traces name the model they called.",
  providers: "Providers appear here once an agent's traces name the provider that served them.",
  endpoints: "Endpoints appear here once the Origin Agent is installed on them and reports in.",
  runtimes: "Local models and runtimes appear here once an endpoint runs a model itself, through a runtime such as Ollama or LM Studio.",
};
const plural = (n, one, many = `${one}s`) => `${fmt(n)} ${n === 1 ? one : many}`;
const stateOf = (q) => (q.data != null ? "ready" : q.loading ? "loading" : "failed");
const nothing = (v) => (v == null ? "—" : fmt(v));

function Chevron({ open }) {
  return (
    <svg viewBox="0 0 10 10" aria-hidden="true" className={`h-[10px] w-[10px] self-center text-[color:var(--muted-soft)] transition-transform duration-[160ms] ${open ? "rotate-90" : ""}`} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3.5 2l3 3-3 3" />
    </svg>
  );
}

// A row's mark: an agent's, a model maker's or an provider's logo, a runtime's where it has one.
function Mark({ row }) {
  if (row.kind === "endpoints") return <span />;
  const model = row.kind === "models" || row.id.startsWith("model:");
  const fallback = row.kind === "providers" ? row.id : model ? makerOf(row.name) : null;
  return <span className={`flex items-center ${row.now ? INK : MUTED}`}><AgentIcon name={row.name} fallback={fallback} className="h-[16px] w-[16px]" /></span>;
}

// What Findings' search finds a row by: the agents and models each case's
// traces ran, and its endpoint (lib/findingsConsole.js caseSearchText).
// Providers and runtimes are named in neither.
const findingsQuery = (row) => (row.kind === "agents" || row.kind === "models" || row.kind === "endpoints" || row.id.startsWith("model:") ? row.full || row.name : null);

function cellOf(row, id) {
  if (id === "change") return moveText(row.now, row.before, fmt);
  if (id === "heard") return row.heard ? formatAgo(new Date(row.heard)) : "—";
  if (id === "models" || id === "agents") return nothing(row.counts?.[id]);
  return nothing(row[id]);
}

// The header: every sortable column a button that sorts it, the arrow on the one sorted by.
function Head({ tab, sort, onSort }) {
  const cols = COLUMNS[tab];
  const head = (id, label, cls = "") => {
    if (!sortable(tab, id)) return <span key={id} className={cls}>{label}</span>;
    const on = sort.key === id;
    const arrow = on ? (sort.dir === "asc" ? "↑" : "↓") : "";
    return (
      <button
        key={id}
        type="button"
        onClick={() => onSort(id)}
        aria-label={on ? `${label}, sorted ${sort.dir === "asc" ? "ascending" : "descending"}` : `Sort by ${label}`}
        className={`flex h-full items-center gap-[4px] ${on ? INK : ""} hover:text-[color:var(--ink)] ${FOCUS} ${cls}`}
        data-fleet-sort={id}
      >
        {label}<span aria-hidden="true" className="w-[8px]">{arrow}</span>
      </button>
    );
  };
  return (
    <div className={`grid h-[28px] items-center border-b border-[color:var(--line)] ${PAD} text-[12px] leading-none ${MUTED} ${GRID[cols.cells.length]} ${NARROW}`} data-fleet-head="">
      <span />
      <span />
      {head("name", cols.name)}
      <span className={BAR} />
      {cols.cells.map(([id, label], i) => head(id, label, `justify-end text-right ${i > 1 ? EXTRA : ""}`))}
    </div>
  );
}

// An open row's words: what it did in the range against the span before.
function said(row, len) {
  const against = row.before == null ? "" : `, against ${fmt(row.before)} in the ${len} before`;
  if (row.id.startsWith("runtime:")) {
    const inst = row.installed ? `Installed on ${plural(row.installed, "endpoint")}.` : "";
    return row.now == null ? `${inst} Whether it ran needs access to endpoint events.`.trim() : `Seen running on ${plural(row.now, "endpoint")} in the ${len}${against}. ${inst}`.trim();
  }
  if (row.id.startsWith("model:")) return `Ran on ${plural(row.now, "endpoint")} in the ${len}${against}, ${plural(row.traces, "trace")}.`;
  const inst = row.installed ? ` Installed on ${plural(row.installed, "endpoint")}.` : "";
  const heard = row.heard ? ` Last heard from ${formatAgo(new Date(row.heard))}.` : "";
  return `${plural(row.now, "trace")} in the ${len}${against}.${inst}${heard}`;
}

// A name longer than this may be cut in its row, so its open panel shows it whole, to copy.
const LONG_NAME = 40;

// What an open row touches, each a way to start the inventory from it, and
// the findings in its traces.
function Panel({ row, len, words, onPivot, onFindings }) {
  const groups = row.rel.filter((g) => g.items.length);
  const q = findingsQuery(row);
  return (
    <div className="grid gap-[12px] bg-[color:var(--panel)] pb-[14px] pl-[52px] pr-[12px] pt-[4px] @max-[740px]:pl-[12px]" role="region" aria-label={row.name} data-fleet-panel={row.id}>
      {row.full || row.name.length > LONG_NAME ? (
        <div className="flex min-w-0 items-start gap-[10px]" data-fleet-full-name="">
          <span className={`min-w-0 select-text break-all font-mono text-[12px] leading-[18px] ${INK}`}>{row.full || row.name}</span>
          <CopyButton value={row.full || row.name} label="Copy name" />
        </div>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-x-[16px] gap-y-[8px]">
        <p className={`m-0 text-[12.5px] leading-[18px] ${MUTED}`}>{said(row, len)}</p>
        {q && row.now ? (
          <button type="button" onClick={() => onFindings(q)} className={BUTTON} data-fleet-findings="">
            Search Findings for<span className="max-w-[240px] truncate">{q}</span>
          </button>
        ) : null}
      </div>
      {groups.length ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-x-[24px] gap-y-[12px]">
          {groups.map((g) => (
            <section key={g.label} className="min-w-0" data-fleet-rel={g.label}>
              <h3 className={`m-0 flex items-baseline gap-[6px] pb-[4px] text-[12px] font-medium ${MUTED}`}>{g.label}<span className="tabular-nums">{fmt(g.items.length)}</span></h3>
              {g.items.slice(0, 8).map((x) => (
                <button key={`${x.kind}:${x.id}`} type="button" onClick={() => onPivot(x)} className={`flex h-[26px] w-full items-center gap-[8px] rounded-[6px] px-[6px] text-left text-[12.5px] hover:bg-[color:var(--gray-fill)] ${FOCUS}`} data-fleet-pivot={x.kind}>
                  <span className={`min-w-0 flex-1 truncate ${INK}`} title={x.name}><Highlight text={x.name} terms={words} /></span>
                  {x.n != null ? <span className={`tabular-nums ${MUTED}`}>{fmt(x.n)}</span> : null}
                </button>
              ))}
              {g.items.length > 8 ? (
                <HoverTooltip placement="bottom" wide label={g.items.slice(8).map((x) => x.name).join(", ")}>
                  <p tabIndex={0} className={`m-0 w-fit px-[6px] pt-[2px] text-[12px] ${MUTED} underline decoration-dotted underline-offset-[3px] ${FOCUS}`}>{fmt(g.items.length - 8)} more</p>
                </HoverTooltip>
              ) : null}
            </section>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function Row({ row, tab, open, max, len, words, onToggle, onPivot, onFindings }) {
  const cols = COLUMNS[tab].cells;
  const via = matchedVia(row, words);
  return (
    <div role="listitem" data-fleet-row={row.id}>
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className={`group grid min-h-[44px] w-full items-center border-b ${PAD} text-left text-[13px] ${GRID[cols.length]} ${NARROW} ${open ? "border-transparent bg-[color:var(--panel)]" : "border-[color:var(--line-soft)] hover:bg-[color:var(--gray-fill)]"} ${FOCUS}`}
      >
        <Chevron open={open} />
        <Mark row={row} />
        <span className="grid min-w-0 gap-[1px] py-[5px] pr-[12px]">
          <span className={`flex min-w-0 items-baseline gap-[8px] ${row.now ? INK : MUTED}`}>
            <span className="truncate" title={row.full || row.name}><Highlight text={row.name} terms={words} /></span>
            {row.state ? <span className={`shrink-0 text-[12px] ${row.state === "new" ? `font-semibold ${INK}` : MUTED}`}><Highlight text={row.state} terms={words} /></span> : null}
          </span>
          {row.sub || via ? (
            <span className={`truncate text-[12px] ${MUTED}`}>
              <Highlight text={row.sub} terms={words} />
              {via ? <>{row.sub ? ", " : ""}{via.label.toLowerCase()} <Highlight text={via.name} terms={words} /></> : null}
            </span>
          ) : null}
        </span>
        <Pair now={row.now || 0} before={row.before || 0} max={max} className={`mr-[24px] ${BAR}`} />
        {cols.map(([id], i) => (
          <span key={id} className={`text-right tabular-nums ${i === 0 ? `font-medium ${INK}` : MUTED} ${i > 1 ? EXTRA : ""}`}>{cellOf(row, id)}</span>
        ))}
      </button>
      {open ? <Panel row={row} len={len} words={words} onPivot={onPivot} onFindings={onFindings} /> : null}
    </div>
  );
}

// The last range's local inference while a new one reads, as Local inference read it (FleetLocal).
function useLocal(win, access) {
  const now = useQuery("getFleetLocal", { range: win.now, access }, { staleMs: FLEET_STALE_MS });
  // No span before where the range and it pass the widest window read (the Overview's rule).
  const prior = useQuery("getFleetLocal", { range: win.then, access, before: true }, { enabled: !!win.then, staleMs: FLEET_STALE_MS });
  const prev = win.then && !prior.previousParams ? prior.data : null;
  const data = useMemo(() => (now.data ? { ...now.data, then: prev?.then ?? null, ranThen: prev?.ranThen ?? null } : null), [now.data, prev]);
  return { data, loading: now.loading, error: now.error };
}

export default function FleetOverview({ range = DEFAULT_RANGE }) {
  const win = fleetWindows(range);
  const w = win.w;
  const caps = useMyPermissions();
  const access = { inventory: hasPermission(caps, PERMISSIONS.ENDPOINT_METADATA_READ), events: hasPermission(caps, PERMISSIONS.EVENTS_READ) };
  const setRange = useSetRange();
  const f = useFleet();
  const tab = TAB_IDS.includes(f.view) ? f.view : "agents";
  const [params, setParams] = useSearchParams();

  // The search and the row open are the page's, across tabs; each tab keeps its own order and fold.
  const [query, setQuery] = useState("");
  const [settled] = useDebouncedValue(query, 300);
  const words = useMemo(() => searchWords(query), [query]);
  const [sorts, setSorts] = useState({});
  const sort = sorts[tab] || DEFAULT_SORT;
  const [openKey, setOpenKey] = useState(null);
  const [all, setAll] = useState({});
  // One endpoint a link or a pivot asked for, until the reader searches.
  const [one, setOne] = useState(null);
  // An agent a link names, opened once the list is read.
  const want = useRef(null);

  // Links arrive once: the address keeps the page's range and nothing else of theirs.
  useEffect(() => {
    const selected = params.get("fleetSelected")?.match(/^endpoint:(.+)$/)?.[1] || null;
    const named = params.get("fleetSearch");
    if (!selected && !named && !params.has("fleetView")) return;
    if (selected) { setFleet({ view: "endpoints" }); setOne(selected); setQuery(""); setOpenKey(`endpoints:${selected}`); }
    else if (named) { setFleet({ view: "agents" }); setQuery(named); want.current = named.toLowerCase(); }
    setParams((p) => { const next = new URLSearchParams(p); for (const k of ["fleetSelected", "fleetSearch", "fleetView"]) next.delete(k); return next; }, { replace: true });
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  // The tiles and the running total: the Overview's reads, over the same windows.
  const traces = useQuery("getOverviewTraces", { days: w.days }, { staleMs: FLEET_STALE_MS });
  const agents = useQuery("getOverviewAgents", { now: w.now, before: w.before }, { staleMs: FLEET_STALE_MS });
  const coverage = useQuery("getFleetCoverage", { now: win.now, then: win.then }, { staleMs: FLEET_STALE_MS });
  const cov = useMemo(() => coverageOf(coverage.data), [coverage.data]);
  const tNow = tracesIn(traces.data, w.now);
  const tBefore = tracesIn(traces.data, w.before);
  const tCompare = tBefore > 0;
  const num = numberer((tNow ?? 0) >= 1e5);
  const agentsCompare = !!agents.data?.before && agents.data.before.some((r) => Number(r.traces) > 0);
  const counts = agents.data ? agentCounts(agentsOf(agents.data), agentsCompare) : null;
  const epNow = coverage.data?.now?.endpoints ?? null;
  const epBefore = coverage.data?.then?.endpoints ?? null;
  const span = spanWords(w.now.from, w.now.to);
  const len = lengthOf(w.now.from, w.now.to - 59_999);
  const room = useRef(null);
  const { width } = useElementSize(room);

  // The inventory: agents and models from Fleet's block, local inference, and a page of the endpoints.
  const { read: blockRead, d } = useBlock(range);
  const local = useLocal(win, access);
  const agentList = useMemo(() => agentRows(d), [d]);
  const modelList = useMemo(() => modelRows(d), [d]);
  const providerList = useMemo(() => providerRows(d), [d]);
  const localList = useMemo(() => localRows(local.data), [local.data]);
  const term = one ? "" : settled.trim();
  const epSort = sorts.endpoints || DEFAULT_SORT;
  const pages = usePagedInventoryRows({
    method: "getFleetEndpoints",
    range,
    search: term,
    sortBy: { id: epSort.key === "name" ? "hostname" : "sessions", dir: epSort.dir },
    endpointIds: one ? [one] : null,
    pageSize: PAGE,
    // Read where it is shown, or where a search needs its count.
    enabled: tab === "endpoints" || term !== "",
  });
  const ids = useMemo(() => pages.rows.map((r) => r.id), [pages.rows]);
  const epAgents = useQuery("getFleetEndpointAgents", { ids, range: win.now }, { enabled: tab === "endpoints" && ids.length > 0, staleMs: FLEET_STALE_MS });
  const epAgentsBefore = useQuery("getFleetEndpointAgents", { ids, range: win.then, before: true }, { enabled: tab === "endpoints" && ids.length > 0 && !!win.then, staleMs: FLEET_STALE_MS });
  const epThen = win.then && !epAgentsBefore.previousParams ? epAgentsBefore.data?.then : null;
  const endpointList = useMemo(
    () => endpointRows(pages.rows, { agents: epAgents.data?.now || [], before: endpointsBefore(epThen), lastSeen: cov.lastSeen, heard: cov.heard }),
    [pages.rows, epAgents.data, epThen, cov.lastSeen, cov.heard],
  );
  const rows = { agents: agentList, models: modelList, providers: providerList, endpoints: endpointList, runtimes: localList };

  const found = (t) => (t === "endpoints" ? rows.endpoints : rows[t].filter((r) => matches(r, words)));
  // The endpoints are searched and counted by the server; the rest here.
  const countOf = (t) => (t !== "endpoints" ? found(t).length : term || one ? pages.total : cov.total ?? pages.total);
  const read = { agents: blockRead, models: blockRead, providers: blockRead, runtimes: local, endpoints: { loading: pages.loading, error: pages.error } }[tab];
  const shown = tab === "endpoints" ? rows.endpoints : sortRows(found(tab), sort);
  const visible = tab === "endpoints" || all[tab] ? shown : shown.slice(0, FIRST);
  const max = Math.max(1, ...shown.map((r) => Math.max(r.now || 0, r.before || 0)));

  // A linked agent opens once it is listed.
  useEffect(() => {
    if (!want.current || !agentList.length) return;
    const hit = agentList.find((r) => r.name.toLowerCase() === want.current);
    want.current = null;
    if (hit) setOpenKey(`agents:${hit.id}`);
  }, [agentList]);

  const doors = useDoors();
  const onTab = (t) => { setFleet({ view: t }); setOpenKey(null); };
  const onSearch = (v) => { setQuery(v); setOne(null); };
  const onSort = (key) => setSorts((s) => ({ ...s, [tab]: nextSort(s[tab] || DEFAULT_SORT, key) }));
  // Start from what an open row touches: its tab, found by name, opened. An
  // endpoint is read by its id, since its name need not be unique.
  const onPivot = (x) => {
    setFleet({ view: x.kind });
    setQuery(x.q);
    setOne(x.kind === "endpoints" ? x.id : null);
    setOpenKey(`${x.kind}:${x.id}`);
  };

  const busy = read.loading && !shown.length;
  const failed = read.error && !shown.length;
  const denied = tab === "endpoints" && pages.denied;

  return (
    <section data-fleet-overview="" className="@container flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-y-auto bg-[color:var(--bg)] px-[var(--main-inset)] text-[color:var(--ink)]">
      <div className="w-full max-w-[1180px] pb-[48px]">
        <header className="flex min-h-[62px] flex-wrap items-center gap-x-[12px] gap-y-[8px] py-[8px]">
          <h1 className="m-0 text-[22px] font-semibold leading-[1.15] tracking-[-0.02em]">Fleet</h1>
          <span className={`text-[13px] tabular-nums ${MUTED}`} data-fleet-span="">{span}</span>
          <span className="flex-1" />
          <span className="w-[160px] shrink-0 @max-[740px]:w-[140px]" data-fleet-range="">
            <DateRangePicker range={range} onRangeChange={setRange} />
          </span>
        </header>

        <div ref={room} aria-busy={traces.loading || agents.loading || coverage.loading ? "true" : undefined}>
          <div className="grid grid-cols-3 border-t border-[color:var(--line-strong)]" data-fleet-tiles="">
            <Tile
              at={0}
              name="traces"
              label="Traces"
              state={stateOf(traces)}
              figure={tNow == null ? "" : num(tNow)}
              change={tCompare ? pctText(tNow, tBefore) : ""}
              before={tCompare ? <><span className="tabular-nums">{num(tBefore)}</span> in the {len} before</> : null}
              tip={<><b>{plural(tNow ?? 0, "trace")}</b> from {span}{tCompare ? `, against ${fmt(tBefore)} in the ${len} before` : ""}.</>}
            />
            <Tile
              at={1}
              name="agents"
              label="Agents"
              state={stateOf(agents)}
              figure={counts ? num(counts.now) : ""}
              change={counts && agentsCompare ? `${num(counts.new)} new` : ""}
              before={counts && agentsCompare ? <><span className="tabular-nums">{num(counts.before)}</span> in the {len} before</> : null}
              tip={counts ? <><b>{plural(counts.now, "agent")}</b> produced traces from {span}{agentsCompare ? `, ${fmt(counts.new)} of them new. ${fmt(counts.before)} produced traces in the ${len} before.` : "."}</> : null}
            />
            <Tile
              at={2}
              name="endpoints"
              label="Endpoints"
              state={epNow != null ? "ready" : coverage.loading ? "loading" : "failed"}
              figure={epNow == null ? "" : num(epNow)}
              change={epBefore ? pctText(epNow, epBefore) : ""}
              before={cov.total != null ? <>of <span className="tabular-nums">{num(cov.total)}</span> enrolled{cov.quiet != null ? <>, <span className="tabular-nums">{num(cov.quiet)}</span> not heard from in the {len}</> : null}</> : null}
              tip={epNow != null ? <><b>{plural(epNow, "endpoint")}</b> sent AI traces from {span}{epBefore ? `, against ${fmt(epBefore)} in the ${len} before` : ""}.{cov.total != null ? ` ${fmt(cov.total)} are enrolled${cov.quiet != null ? `, and ${fmt(cov.quiet)} sent nothing at all in the range` : ""}.` : ""}</> : null}
            />
          </div>
          <RunningTotal state={stateOf(traces)} series={traces.data ? runningTotal(traces.data, w) : null} unit={w.unit} num={num} compare={tCompare} len={len} width={Math.floor(width)} height={140} />
        </div>

        <div className="flex flex-wrap items-center gap-x-[16px] gap-y-[8px] pb-[10px] pt-[24px]">
          <Tabs
            className="mr-auto"
            label="Start from"
            value={tab}
            onChange={onTab}
            itemAttrs={(t) => ({ "data-fleet-tab": t.id })}
            items={TABS.map(([id, label]) => {
              const n = countOf(id);
              return { id, label: <span className="flex items-center gap-[7px]">{label}<span className="text-[11.5px] tabular-nums text-[color:var(--muted-soft)]">{n == null ? "" : fmt(n)}</span></span> };
            })}
          />
          <div className="min-w-[260px] max-w-[560px] flex-[1_1_360px]" data-fleet-search="">
            <SearchInput size="sm" value={query} onChange={onSearch} placeholder="Search" label="Search the fleet" className="h-[32px] w-full" />
          </div>
        </div>

        <div role="list" aria-label={TABS.find(([id]) => id === tab)[1]} aria-busy={read.loading ? "true" : undefined} data-fleet-inventory={tab}>
          <Head tab={tab} sort={sort} onSort={onSort} />
          {denied ? <p className={`m-0 py-[24px] text-[13px] ${MUTED}`}>Listing endpoints needs access to endpoint metadata.</p>
            : failed ? <p role="alert" className="m-0 py-[24px] text-[13px] text-[color:var(--severity-critical)]">{TABS.find(([id]) => id === tab)[1]} could not be read. Reload to try again.</p>
              : busy ? <SkeletonRows rows={8} rowClassName={`grid h-[44px] items-center ${PAD} ${GRID[COLUMNS[tab].cells.length]} ${NARROW}`} cells={["w-0", "w-0", "w-[60%]", "w-[50%]", "ml-auto w-[36px]", "ml-auto w-[40px]"]} label={`Loading ${tab}…`} />
                : !shown.length ? <p className={`m-0 py-[24px] text-[13px] ${MUTED}`} data-fleet-empty="">{query.trim() ? "Nothing in this view matches the search." : EMPTY[tab]}</p>
                  : visible.map((r) => {
                    const key = `${tab}:${r.id}`;
                    return <Row key={key} row={r} tab={tab} open={openKey === key} max={max} len={len} words={words} onToggle={() => setOpenKey(openKey === key ? null : key)} onPivot={onPivot} onFindings={doors.findingsFor} />;
                  })}
          {tab !== "endpoints" && !all[tab] && shown.length > FIRST ? (
            <button type="button" onClick={() => setAll((a) => ({ ...a, [tab]: true }))} className={`mt-[8px] h-[32px] rounded-[7px] px-[12px] text-[12.5px] ${MUTED} hover:text-[color:var(--ink)] ${FOCUS}`} data-fleet-more="">Show all {fmt(shown.length)}</button>
          ) : null}
          {tab === "endpoints" && pages.hasMore ? (
            <button type="button" disabled={pages.loading} onClick={() => pages.loadMore?.()} className={`mt-[8px] h-[32px] rounded-[7px] px-[12px] text-[12.5px] ${MUTED} hover:text-[color:var(--ink)] ${FOCUS}`} data-fleet-more="">Show {fmt(Math.min(PAGE, (pages.total ?? 0) - shown.length))} more of {fmt(pages.total)}</button>
          ) : null}
        </div>
      </div>
    </section>
  );
}
